import React, { useEffect } from 'react';

/**
 * Small, dependency-free Helmet helper.
 * Improvements:
 * - Accepts a single child or multiple children (uses React.Children.toArray)
 * - Supports common JSX prop names like className -> class
 * - Skips non-string element types (components/functions)
 * - Cleans up added head nodes on unmount
 */
export default function Helmet({ children }) {
  useEffect(() => {
    const elements = [];
    const arr = React.Children.toArray(children);

    arr.forEach((child) => {
      if (!child) return;

      const tag = typeof child.type === 'string' ? child.type : null;
      if (!tag) return; // skip React components or fragments

      const el = document.createElement(tag);

      const props = child.props || {};
      Object.keys(props).forEach((key) => {
        if (key === 'children') {
          // Set text content for tags like <title> or <script>
          if (props.children !== undefined && props.children !== null) {
            el.textContent = props.children;
          }
        } else {
          // normalize common React prop names
          const attrName = key === 'className' ? 'class' : key;
          // Boolean attributes (e.g., async) should be set as attribute name when true
          const value = props[key];
          if (typeof value === 'boolean') {
            if (value) el.setAttribute(attrName, '');
          } else if (value !== undefined && value !== null) {
            el.setAttribute(attrName, String(value));
          }
        }
      });

      document.head.appendChild(el);
      elements.push(el);
    });

    return () => {
      elements.forEach((el) => el.remove());
    };
  }, [children]);

  return null;
}
